import AuthAccessValidator from '../Auth/AuthAccessValidator'

/**
 * Componente de cronómetro para medir el tiempo de resolución de un ticket.
 * * **Funcionalidades**:
 * - Visualización del tiempo transcurrido con formato personalizado.
 * - Controles de inicio, pausa y reinicio (Start/Stop).
 * - Indicador visual de estado mediante un punto con animación de pulso cuando está activo.
 * - Validación de permisos mediante `AuthAccessValidator` para mostrar u ocultar controles.
 * - Renderizado condicional basado en el estado `completed` del ticket.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Function} props.formatTime - Función que recibe un valor numérico y retorna un string formateado (ej: "00:15:30").
 * @param {boolean} props.running - Estado booleano que indica si el cronómetro está en marcha.
 * @param {Function} props.stop - Función para detener o reiniciar el cronómetro.
 * @param {number} props.time - El valor del tiempo actual (normalmente en segundos o milisegundos).
 * @param {Object} props.ticket - Objeto con la información del ticket actual.
 * @param {boolean} props.ticket.completed - Si el ticket está marcado como completado, se ocultan los controles.
 * @param {Function} props.start - Función para iniciar el cronómetro.
 * * @returns {JSX.Element} Una fila compacta con el contador de tiempo y botones protegidos por permisos.
 */
const TicketTime = ({ formatTime, running, stop, time, ticket, start }) => {
  return (
    <div className="tmr">
      <div className="d-flex align-items-center gap-2 min-w-0">
        <span className={`tmr-dot ${running ? 'is-running' : ''}`} />
        <div className="d-flex flex-column min-w-0">
          <span className="tmr-label">Tiempo de resolución</span>
          <span className="tmr-value">{formatTime(time)}</span>
        </div>
      </div>

      <AuthAccessValidator AccessNames={['ticket_support']} showError={false}>
        {!ticket.completed && (
          <div className="d-flex align-items-center gap-1">
            <button
              onClick={running ? stop : start}
              className={`btn btn-sm tmr-btn ${
                running ? 'btn-warning text-white' : 'btn-success'
              }`}
              title={running ? 'Pausar' : 'Iniciar'}
            >
              <i className={`fa-solid ${running ? 'fa-pause' : 'fa-play'}`}></i>
            </button>

            <button
              onClick={stop}
              disabled={!running && time === 0}
              className="btn btn-sm tmr-btn tmr-reset"
              title="Reiniciar"
            >
              <i className="fa-solid fa-rotate-left"></i>
            </button>
          </div>
        )}
      </AuthAccessValidator>

      <style>{`
        .tmr {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .tmr-dot {
          width: 8px;
          height: 8px;
          flex-shrink: 0;
          border-radius: 50%;
          background: var(--gm-text-faint);
        }
        .tmr-dot.is-running {
          background: var(--gm-green);
          animation: pulse-green 2s infinite;
        }
        .tmr-label {
          font-size: 0.72rem;
          color: var(--gm-text-muted);
          line-height: 1.2;
        }
        .tmr-value {
          font-family: var(--bs-font-monospace);
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--gm-text);
          line-height: 1.3;
        }
        .tmr-btn {
          width: 28px;
          height: 28px;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          font-size: 0.72rem;
        }
        .tmr-reset {
          color: var(--gm-text-muted);
          border: 1px solid var(--gm-border);
          background: transparent;
        }
        .tmr-reset:hover:not(:disabled) {
          color: var(--gm-text);
          background: var(--gm-surface-2);
        }

        @keyframes pulse-green {
          0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.6); }
          70% { box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); }
          100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
        }
      `}</style>
    </div>
  )
}

export default TicketTime
